import { AnyAction, createAction, createReducer } from '@reduxjs/toolkit'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { throttle } from 'lodash'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Dimensions } from 'react-native'
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view'
import { ms, ScaledSheet } from 'react-native-size-matters'
import useSWR from 'swr'
import { CommunityImageTagResourceCollection } from '~api/types'
import { FormButton } from '~components/FormButton'
import { FormInput } from '~components/FormInput'
import Loading from '~components/Loading'
import ModalScreen, { getModalStyleDefinition, ModalFooter } from '~components/ModalScreen'
import TagsInput from '~components/TagsInput'
import { Text, View } from '~components/Themed'
import { doc, firestore, functions, httpsCallable, onSnapshot } from '~providers/firebase'
import RollbarErrorTracking from '~providers/rollbar'
import ChatService from '~services/chat'
import { useTheme } from '~theme/ThemeManager'
import { ExistingChat, Tag } from '~types'
import fetch from '~utils/fetch'

export interface State {
  path: string
  isLoading: boolean
  chat?: ExistingChat
}

export const initialState: State = {
  path: '',
  isLoading: true,
  chat: undefined,
}

export const setLoading = createAction<boolean>('setLoading')
export const setChat = createAction<ExistingChat>('setChat')

const reducer = createReducer(initialState, (builder) =>
  builder
    .addCase(setLoading, (state, action) => {
      state.isLoading = action.payload
    })
    .addCase(setChat, (state, action) => {
      state.chat = action.payload
      state.isLoading = false
    }),
)
const store = React.createContext<[State, React.Dispatch<AnyAction>]>([initialState, () => {}])
const { Provider } = store

export default function EditChatScreen(): React.JSX.Element {
  const styles = useStyles()
  const { folderId, chatId } = useLocalSearchParams<{
    folderId: string
    chatId: string
  }>()
  const router = useRouter()
  const [state, dispatch] = React.useReducer(reducer, {
    ...initialState,
    path: `classes/${folderId}/chats/${chatId}`,
  })
  const value: [State, React.Dispatch<AnyAction>] = [state, dispatch]

  const [name, setName] = useState('')
  const [tags, setTags] = useState<Tag[]>([])
  const [query, setQuery] = useState('')

  const throttled = useRef(throttle(setQuery, 500))

  const { data: suggestions, error: tagError } = useSWR<Tag[]>(
    () => {
      const args = new URLSearchParams()
      if (query.length) {
        args.append('query', query)
      }
      tags.forEach((tag) => args.append('exclude[]', tag.value as string))

      return `/api/v1/customer/templates/tags?${args.toString()}`
    },
    async (url: string) => {
      const response = await fetch<CommunityImageTagResourceCollection>(url)
      return response.data.map((tag) => ({
        label: tag.name,
        value: tag.name,
      }))
    },
    {
      fallbackData: [],
    },
  )

  if (tagError) {
    console.error('Error fetching tags:', tagError)
  }

  useEffect(() => {
    dispatch(setLoading(true))
    let cancel = false

    const subscriber = onSnapshot(
      doc(firestore, state.path),
      (snapshot) => {
        if (typeof snapshot.exists === 'function' ? !snapshot.exists() : !snapshot.exists) {
          router.canGoBack() && router.back()
        } else if (!cancel) {
          const chat = ChatService.chatFromSnapshot(snapshot)
          dispatch(setChat(chat))
        }
      },
      () => {
        router.canGoBack() && router.back()
      },
    )

    return () => {
      cancel = true
      subscriber()
    }
  }, [dispatch, router, state.path])

  const uploadTemplate = async () => {
    dispatch(setLoading(true))

    try {
      await httpsCallable(
        functions,
        'uploadTemplate',
      )({
        path: state.path,
        name,
        tags: tags.map((tag) => tag.value),
      })
      router.canGoBack() && router.back()
    } catch (error) {
      RollbarErrorTracking.error(error as Error)
    }

    dispatch(setLoading(false))
  }

  const handleQueryChanged = useCallback((query: string) => {
    throttled.current(query)
  }, [])

  const addNewTag = useCallback((tag: Tag) => {
    setTags((tags) => [...tags, tag])
  }, [])

  const onDelete = useCallback((index: number) => {
    setTags((tags) => tags.filter((_, i) => i !== index))
  }, [])

  useEffect(() => {
    setName(state.chat?.name ?? '')
  }, [state.chat, setName])

  return (
    <Provider value={value}>
      <ModalScreen
        ContainerComponent={React.Fragment}
        Footer={
          <ModalFooter>
            <View></View>
            <FormButton
              title="Upload as template"
              onPress={uploadTemplate}
              disabled={state.isLoading || name.length === 0 || tags.length === 0}
            />
          </ModalFooter>
        }
      >
        {!state.chat ? (
          <Loading />
        ) : (
          <KeyboardAwareScrollView>
            <View style={styles.inner}>
              <View style={styles.content}>
                <View style={styles.titleContainer}>
                  <Text style={styles.title}>Upload Chat to School Templates</Text>
                </View>
                <View>
                  <FormInput
                    label="Name of chat"
                    value={name}
                    onChangeText={setName}
                    maxLength={128}
                  />
                </View>
                <View>
                  <TagsInput
                    onAdd={addNewTag}
                    onDelete={onDelete}
                    suggestions={suggestions ?? []}
                    selected={tags}
                    onInput={handleQueryChanged}
                  />
                </View>
              </View>
            </View>
          </KeyboardAwareScrollView>
        )}
      </ModalScreen>
    </Provider>
  )
}

const useStyles = () => {
  const { theme, mode } = useTheme()
  return ScaledSheet.create({
    ...getModalStyleDefinition(theme, mode),
    container: {
      backgroundColor: theme[100],
      flexGrow: 0,
      width: Math.min(Dimensions.get('window').width * 0.8, ms(256)),
    },
    emptyContainer: {
      paddingHorizontal: '10@ms',
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
    },
    listContainer: {
      backgroundColor: theme[100],
      paddingHorizontal: '10@mvs0.8',
      paddingVertical: '10@mvs0.8',
      minHeight: Dimensions.get('window').height,
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    titleContainer: {
      marginBottom: '10@mvs0.8',
    },
    title: {
      fontSize: '18@ms',
      fontWeight: '700',
      textAlign: 'center',
    },
    subtitle: {
      fontSize: '12@ms0.4',
      fontWeight: '300',
      textAlign: 'center',
    },
    tagDescription: {
      fontSize: '8@ms0.4',
      fontWeight: '200',
    },
    formInput: {
      backgroundColor: theme.white,
    },
    tagContainer: {
      marginEnd: '4@ms',
      marginVertical: '2@ms',
    },
    tag: {
      backgroundColor: theme[400],
      paddingVertical: '6@mvs',
      paddingHorizontal: '4@ms',
    },
    tagButton: {
      borderRadius: '30px',
    },
    tagActive: {
      backgroundColor: theme.primary,
    },
    tagTitle: {
      fontSize: '10@ms0.2',
      color: theme.white,
    },
    tagSuggestionsStyle: {
      marginBottom: '16@mvs',
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: '4@mvs',
    },
    tagContainerStyle: {
      marginVertical: '8@mvs',
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: '4@mvs',
    },
    itemSeparator: {
      height: '5@mvs0.8',
    },
    label: {
      fontSize: '10@ms0.3',
      fontWeight: '500',
      marginBottom: '3@mvs',
      color: theme[500],
    },
    tagInput: {
      width: '100%',
    },
  })
}
